import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Spinner } from '../../../components/UI';
import { bookingApi, ICrmError, isCrmError } from '../../../services/crm';
import { dateKey, DAYS_LONG, keyParts, MONTHS_LONG, timeIn, zonedParts, zonedTimeToUtc, zoneLabel, timeZoneOptions } from '../../sales/shared/calendar.helpers';

export interface SlotPickerProps {
    slug: string;
    typeId: number;
    timeZone: string;
    onTimeZone: (tz: string) => void;
    onPick: (startIso: string) => void;
    /** Bump to refetch (e.g. after a 409). */
    refreshKey?: number;
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** First instant of a month in a zone. */
const monthStart = (y: number, m: number, tz: string): Date => zonedTimeToUtc(y, m, 1, 0, 0, tz);
const nextMonth = (y: number, m: number): [number, number] => (m === 12 ? [y + 1, 1] : [y, m + 1]);
const prevMonth = (y: number, m: number): [number, number] => (m === 1 ? [y - 1, 12] : [y, m - 1]);

// #region SlotPicker
/**
 * Month grid on the left, the chosen day's times on the right. Fetches one
 * month of openings per request and caches it, so paging back is instant.
 */
export const SlotPicker: React.FunctionComponent<SlotPickerProps> = ({ slug, typeId, timeZone, onTimeZone, onPick, refreshKey = 0 }): React.ReactElement => {
    const today = zonedParts(new Date(), timeZone);
    const [ym, setYm] = React.useState<[number, number]>([today.year, today.month]);
    const [byDay, setByDay] = React.useState<Record<string, string[]>>({});
    const [loaded, setLoaded] = React.useState<Record<string, boolean>>({});
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedDay, setSelectedDay] = React.useState<string | null>(null);

    // A zone change moves every slot to a different local day; start over.
    React.useEffect(() => { setByDay({}); setLoaded({}); setSelectedDay(null); }, [timeZone, typeId, refreshKey]);

    const [y, m] = ym;
    const monthKey = `${y}-${m}`;

    React.useEffect(() => {
        if (loaded[monthKey]) return;
        let cancelled = false;
        const from = monthStart(y, m, timeZone);
        const [ny, nm] = nextMonth(y, m);
        const to = monthStart(ny, nm, timeZone);
        const start = new Date(Math.max(from.getTime(), Date.now()));
        if (start >= to) { setLoaded((p) => ({ ...p, [monthKey]: true })); return; }
        setLoading(true);
        setError(null);
        bookingApi.slots(slug, typeId, start.toISOString(), to.toISOString()).then((result) => {
            if (cancelled) return;
            setLoading(false);
            if (isCrmError(result)) { setError((result as ICrmError).message); return; }
            setByDay((prev) => {
                const next = { ...prev };
                for (const iso of result.slots) {
                    const k = dateKey(new Date(iso), timeZone);
                    (next[k] = next[k] ?? []).push(iso);
                }
                return next;
            });
            setLoaded((p) => ({ ...p, [monthKey]: true }));
        });
        return () => { cancelled = true; };
    }, [slug, typeId, timeZone, y, m, monthKey, loaded]);

    // Pick the first open day of the month automatically, so a time is one click away.
    React.useEffect(() => {
        if (!loaded[monthKey] || selectedDay) return;
        const first = Object.keys(byDay).filter((k) => k.startsWith(`${y}-${String(m).padStart(2, '0')}`)).sort()[0];
        if (first) setSelectedDay(first);
    }, [loaded, monthKey, byDay, selectedDay, y, m]);

    const firstWeekday = zonedParts(monthStart(y, m, timeZone), timeZone).weekday;   // 0 = Sun
    const lead = (firstWeekday + 6) % 7;                                             // cells before the 1st, Monday-first
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const todayKey = dateKey(new Date(), timeZone);
    const monthHasSlots = Object.keys(byDay).some((k) => k.startsWith(`${y}-${String(m).padStart(2, '0')}`));
    const canGoBack = y > today.year || (y === today.year && m > today.month);

    const daySlots = selectedDay ? (byDay[selectedDay] ?? []).slice().sort() : [];
    const selectedLabel = selectedDay ? (() => { const p = keyParts(selectedDay); const wd = zonedParts(zonedTimeToUtc(p.y, p.m, p.d, 12, 0, timeZone), timeZone).weekday; return `${DAYS_LONG[wd]}, ${MONTHS_LONG[p.m - 1]} ${p.d}`; })() : '';

    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_200px]">
            <div>
                <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{MONTHS_LONG[m - 1]} {y}</div>
                    <div className="flex items-center gap-x-1">
                        <button type="button" aria-label="Previous month" disabled={!canGoBack} onClick={() => setYm(prevMonth(y, m))}
                            className="rounded-md p-1.5 text-zinc-600 hover:bg-surface-muted disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:text-zinc-300">
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label="Next month" onClick={() => setYm(nextMonth(y, m))}
                            className="rounded-md p-1.5 text-zinc-600 hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:text-zinc-300">
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div className="mt-3 grid grid-cols-7 gap-1 text-center">
                    {DOW.map((d) => <div key={d} className="py-1 text-[11px] font-medium uppercase tracking-wide text-zinc-400">{d}</div>)}
                    {Array.from({ length: lead }).map((_, i) => <div key={`lead-${i}`} />)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                        const d = i + 1;
                        const key = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const open = (byDay[key]?.length ?? 0) > 0;
                        const isToday = key === todayKey;
                        const selected = key === selectedDay;
                        return (
                            <button key={key} type="button" disabled={!open} onClick={() => setSelectedDay(key)} aria-pressed={selected}
                                className={[
                                    'mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm tabular-nums transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600',
                                    selected ? 'bg-emerald-600 font-semibold text-white' : open ? 'bg-emerald-600/10 font-semibold text-emerald-800 hover:bg-emerald-600/20 dark:text-emerald-300' : 'text-zinc-400 dark:text-zinc-600',
                                    isToday && !selected ? 'ring-1 ring-inset ring-zinc-400 dark:ring-zinc-500' : ''
                                ].join(' ')}>
                                {d}
                            </button>
                        );
                    })}
                </div>

                {loading ? <div className="mt-3 flex items-center gap-x-2 text-xs text-zinc-500"><Spinner show={true} size={16} /> Finding open times…</div> : null}
                {!loading && loaded[monthKey] && !monthHasSlots ? <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">No times this month — try the next one.</p> : null}
                {error ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}

                <label className="mt-4 block text-xs text-zinc-500 dark:text-zinc-400">
                    Time zone
                    <select value={timeZone} onChange={(ev) => onTimeZone(ev.target.value)}
                        className="mt-1 block w-full rounded-lg border border-surface-border bg-surface px-2.5 py-1.5 text-sm text-zinc-900 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 dark:text-zinc-100">
                        {timeZoneOptions().map((tz) => <option key={tz} value={tz}>{zoneLabel(tz)}</option>)}
                    </select>
                </label>
            </div>

            <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{selectedDay ? selectedLabel : 'Pick a day'}</div>
                <div className="mt-3 flex max-h-80 flex-col gap-y-2 overflow-y-auto pr-1 md:max-h-96">
                    {daySlots.map((iso) => (
                        <button key={iso} type="button" onClick={() => onPick(iso)}
                            className="w-full rounded-lg border border-emerald-600/60 px-3 py-2 text-sm font-medium tabular-nums text-emerald-800 transition hover:bg-emerald-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:text-emerald-300 dark:hover:text-white">
                            {timeIn(iso, timeZone)}
                        </button>
                    ))}
                    {selectedDay && daySlots.length === 0 && !loading ? <p className="text-sm text-zinc-500">Nothing open that day.</p> : null}
                </div>
            </div>
        </div>
    );
};
